import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Route42Logo } from '../../../components/brand/Route42Logo';
import { loadCanonicalTourFixture, TourDataBoundary, type TourFixture } from '../data/tour-data';
import { TourGuideProvider } from '../guide/tour-guide';
import { useModalFocusTrap } from '../accessibility/use-modal-focus-trap';
import { TourSidebar } from './TourSidebar';
import { TourTopBar } from './TourTopBar';

export function TourShell() {
  const [fixture, setFixture] = useState<TourFixture | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useModalFocusTrap(mobileNavRef, mobileNavOpen, closeMobileNav);

  useEffect(() => {
    let active = true;
    void loadCanonicalTourFixture()
      .then((value) => {
        if (active) setFixture(value);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : 'The Product Tour example could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, []);

  if (error) return <TourLoadError message={error} />;
  if (!fixture) return <TourLoading />;

  return (
    <TourDataBoundary fixture={fixture}>
      <TourGuideProvider>
      <div className="r42-app-shell r42-tour-shell" data-tour-contract={fixture.contract_id}>
        <TourTopBar machineName={fixture.machine.machine_name} onMenu={() => setMobileNavOpen(true)} />
        <TourSidebar machineName={fixture.machine.machine_name} />
        <main className="r42-main r42-tour-main">
          <div className="r42-tour-safety-banner" role="note">
            <i className="bi bi-stars" aria-hidden="true" />
            <div>
              <strong>Guided example</strong>
              <span>You're exploring fictional, fixed example data. Nothing here changes your organization's information.</span>
            </div>
          </div>
          <Outlet />
        </main>
        {mobileNavOpen ? (
          <div className="r42-mobile-nav-layer" role="dialog" aria-modal="true" aria-label="Product Tour navigation">
            <button className="r42-mobile-nav-backdrop" onClick={closeMobileNav} aria-label="Close Product Tour navigation" />
            <div ref={mobileNavRef} className="r42-mobile-nav-panel" tabIndex={-1}>
              <div className="r42-mobile-nav-head">
                <Route42Logo variant="lockup" />
                <button data-autofocus="true" className="r42-icon-button" onClick={closeMobileNav} aria-label="Close Product Tour navigation">
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
              <div onClick={closeMobileNav}>
                <TourSidebar machineName={fixture.machine.machine_name} />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      </TourGuideProvider>
    </TourDataBoundary>
  );
}

function TourLoading() {
  return (
    <main className="r42-boot" aria-live="polite">
      <Route42Logo variant="mark" />
      <p>Opening the Route42 guided example…</p>
    </main>
  );
}

function TourLoadError({ message }: { message: string }) {
  return (
    <main className="r42-auth-page">
      <section className="r42-auth-card" role="alert">
        <div className="r42-auth-task">
          <h1>Product Tour unavailable</h1>
          <p>{message}</p>
        </div>
        <a className="btn r42-btn-primary" href="/machines">Return to my machines</a>
      </section>
    </main>
  );
}
